import { useCallback, useEffect, useState } from 'react';
import { messengersApi } from 'apis/messengers';
import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { messengerSettingsActions } from '../messengerSettings.actions';
import { selectKnowledgeState } from '../messengerSettings.selectors';
import { useAddBoardIdIfNeeded } from './useAddBoardIdIfNeeded';
import { formatFormValues } from './utils';
export function useMessengerSettings(messengerId) {
    var _a, _b;
    const knowledgeState = useAppSelector(selectKnowledgeState);
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const dispatch = useAppDispatch();
    const messengerApi = useDixaMessengerApi();
    const createBoardIfNeeded = useAddBoardIdIfNeeded(messengerApi, messengerId);
    const [isLoading, setIsLoading] = useState(false);
    const [messenger, setMessenger] = useState(null);
    const setMessengerCallback = useCallback((messenger) => {
        setMessenger(messenger);
        if (messenger.settings) {
            dispatch(messengerSettingsActions.initialize(messenger.settings));
        }
    }, [dispatch, setMessenger]);
    const { data: fallbackSettings } = messengersApi.useReadGlobalFallbackSettingsQuery();
    const goBackToMessengerListPage = useCallback(() => navigate({ path: '/settings/dixa-messenger' }), [navigate]);
    const notifyError = useCallback((message) => notify({ title: message, type: 'error' }), [notify]);
    useEffect(() => {
        if (!messengerId) {
            goBackToMessengerListPage();
            return;
        }
        setIsLoading(true);
        messengerApi
            .fetch(messengerId)
            .then(createBoardIfNeeded)
            .then(setMessengerCallback)
            .catch(() => {
            notifyError('Failed to load the settings');
            goBackToMessengerListPage();
        })
            .finally(() => setIsLoading(false));
    }, [
        messengerId,
        notifyError,
        goBackToMessengerListPage,
        createBoardIfNeeded,
        messengerApi,
        dispatch,
        setMessengerCallback,
    ]);
    const setSettings = useCallback((values) => {
        if (!messengerId) {
            notifyError('Failed to save the settings due to unidentified messenger');
            return;
        }
        setIsLoading(true);
        const formattedValues = formatFormValues(values, knowledgeState);
        messengerApi
            .patchSettings(messengerId, formattedValues)
            .then((messenger) => {
            notify({
                title: 'Settings updated successfully',
                type: 'success',
                time: 3000,
            });
            setMessengerCallback(messenger);
        })
            .catch(() => notifyError('Failed to save the settings'))
            .finally(() => setIsLoading(false));
    }, [
        messengerId,
        notifyError,
        notify,
        messengerApi,
        knowledgeState,
        setMessengerCallback,
    ]);
    return {
        isLoading,
        setSettings,
        messenger,
        bulletinBoardId: ((_b = (_a = messenger === null || messenger === void 0 ? void 0 : messenger.settings) === null || _a === void 0 ? void 0 : _a.integrations.bulletinBoard) === null || _b === void 0 ? void 0 : _b.boardId) || null,
        fallbackSettings,
    };
}
